import React from 'react';
import { motion, useAnimation } from 'framer-motion';
import { HugeiconsIcon } from '@hugeicons/react';
import { FloppyDiskIcon } from '@hugeicons-pro/core-stroke-sharp';
import { FloppyDiskIcon as FloppyDiskIconSolid } from '@hugeicons-pro/core-solid-sharp';

import './MenuItem.css';
import './Button.css';

// Individual Menu Item component
function MenuItem({ item, onClick, onHoverChange, isHovered, isCurrent = false, disableHoverEvents = false }) {
  const iconControls = useAnimation();
  const labelControls = useAnimation();

  React.useEffect(() => {
    if (isHovered) {
      // Icon: smooth up, then gentle spring back down
      iconControls.stop();
      iconControls.set({ y: 0 });
      iconControls
        .start({ y: -2, transition: { type: 'tween', ease: 'easeOut', duration: 0.12 } })
        .then(() => iconControls.start({ y: 0, transition: { type: 'spring', stiffness: 320, damping: 18, mass: 0.45 } }));

      // Label: same motion, slightly staggered
      labelControls.stop();
      labelControls.set({ y: 0 });
      labelControls
        .start({ y: -2, transition: { type: 'tween', ease: 'easeOut', duration: 0.12, delay: 0.05 } })
        .then(() => labelControls.start({ y: 0, transition: { type: 'spring', stiffness: 320, damping: 18, mass: 0.45 } }));
    } else {
      iconControls.stop();
      labelControls.stop();
      iconControls.set({ y: 0 });
      labelControls.set({ y: 0 });
    }
  }, [isHovered, iconControls, labelControls]);

  const ButtonInner = (
    <div className="menu-item-row">
      <motion.span className="menu-button-icon" animate={iconControls}>
        {(() => {
          const hoverColor = '#E0F27E';
          const iconColor = isHovered ? hoverColor : (item.iconColor || 'currentColor');
          const iconRenderer = item.icon;
          if (iconRenderer) {
            return iconRenderer({ size: 18, color: iconColor, hovered: isHovered });
          }
          // Fallback icon (stroke); color still flips to orange on hover
          const hoverIcon = isHovered ? FloppyDiskIconSolid : FloppyDiskIcon;
          return <HugeiconsIcon icon={hoverIcon} size={18} color={iconColor} />;
        })()}
      </motion.span>
      <motion.span className="menu-button-label" aria-label={item.label} animate={labelControls}>{item.label}</motion.span>
      {item.accessory && (
        <span className="menu-item-accessory">{typeof item.accessory === 'function' ? item.accessory() : item.accessory}</span>
      )}
    </div>
  );

  const className = `menu-item ui-button ${isHovered ? 'is-hovered' : ''} ${isCurrent ? 'is-current' : ''}`.trim();

  const sharedProps = {
    className,
    'aria-label': item.label,
    ...(disableHoverEvents ? {} : { onMouseEnter: () => { onHoverChange && onHoverChange(item.id); } })
  };

  if (item.href) {
    return (
      <motion.a {...sharedProps} href={item.href} target={item.target} rel={item.rel} data-id={item.id}>
        {ButtonInner}
      </motion.a>
    );
  }

  return (
    <motion.a {...sharedProps} onClick={() => (item.onClick ? item.onClick() : onClick(item.id))} data-id={item.id}>
      {ButtonInner}
    </motion.a>
  );
}

export default MenuItem;

